import { api } from "./client";
import type { ApiResponse, User } from "./types";

// ============================================================================
// Developer tools — the resettable test members.
//
// A test member is a real account that can be put back at any stage, so
// onboarding, a new member's Home and every empty state can be walked again
// and again. There are two: `first-run`, the new member, and `peer`, a second
// member it can wave at and talk to. They are hidden from every real member
// and cannot reach one; the demo account in particular never sees them. They
// can reach each other. Every call needs the dev key, and none of them exists
// on production.
// ============================================================================

export const TEST_MEMBER_STAGES = [
  "fresh",
  "named",
  "located",
  "onboarded-empty",
  "discoverable",
  "waved-at",
  "new-account",
] as const;

export type TestMemberStage = (typeof TEST_MEMBER_STAGES)[number];

export const TEST_MEMBER_KEYS = ["first-run", "peer"] as const;

export type TestMemberKey = (typeof TEST_MEMBER_KEYS)[number];

export interface DevSession {
  token: string;
  expiresIn: number;
  user: User & { isNewUser: boolean };
}

const devHeaders = (devKey: string, persona?: string) => ({
  "X-Dev-Key": devKey,
  ...(persona ? { "X-Dev-Persona": persona } : {}),
});

/** POST /auth/dev-token with X-Dev-Persona — sign in as the test member. */
export const signInAsTestMember = (devKey: string, persona: string) =>
  api.post<ApiResponse<DevSession>>("/auth/dev-token", undefined, {
    headers: devHeaders(devKey, persona),
  });

/**
 * POST /dev/test-members/:persona/reset — put it back at a stage.
 * Resolves to undefined for `new-account` (204: no account, no session).
 */
export const resetTestMember = (devKey: string, persona: string, stage: TestMemberStage) =>
  api.post<ApiResponse<DevSession> | undefined>(
    `/dev/test-members/${encodeURIComponent(persona)}/reset`,
    { stage },
    { headers: devHeaders(devKey) },
  );
